'use client'

import { useEffect, useRef, useState } from 'react'

import { GameShell } from '@/components/losi-games/game-shell'

import { mountGameCanvas } from '@/lib/losi-games/canvas'
import { difficultyScale, type Difficulty } from '@/lib/losi-games/difficulty'
import { drawChevronShip, drawEyeCapsule } from '@/lib/losi-games/glyphs'
import { bestLocalScore, recordScoreAndWhisper } from '@/lib/losi-games/leaderboard'
import { resolveGameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

export function EyeFlapGame({ colorId = 'creme' }: { colorId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(0)
  const [menu, setMenu] = useState(true)
  const [over, setOver] = useState(false)
  const [whisper, setWhisper] = useState<LosiGameRankWhisper | null>(null)
  const [theme, setTheme] = useState(() => resolveGameTheme(colorId))
  const themeRef = useRef(theme)
  const startFn = useRef<(d: Difficulty) => void>()
  const restartFn = useRef<() => void>()

  useEffect(() => {
    const next = resolveGameTheme(colorId)
    themeRef.current = next
    setTheme(next)
  }, [colorId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    themeRef.current = resolveGameTheme(colorId)
    let cleanup: (() => void) | undefined

  setBest(bestLocalScore('eye-flap'))
  const { ctx, size, dispose } = mountGameCanvas(canvas)

  type Pipe = { x: number; gapY: number; gap: number; scored: boolean }
  const bird = { x: 72, y: 0, vy: 0, size: 18, ang: 0 }
  const pipes: Pipe[] = []
  let scoreAcc = 0
  let gameOver = false
  let playing = false
  let spawnAcc = 0
  let last = performance.now()
  let alive = true
  let raf = 0
  let started = false
  let gapBase = 118
  let speedBase = 140
  let spawnEvery = 1.35

  const resetBoard = () => {
    const { h } = size()
    bird.y = h * 0.42
    bird.vy = 0
    bird.ang = 0
    pipes.length = 0
    scoreAcc = 0
    setScore(0)
    gameOver = false
    setOver(false)
    setWhisper(null)
    started = false
    spawnAcc = 0
  }

  const endRun = () => {
    if (gameOver) return
    gameOver = true
    playing = false
    setOver(true)
    setWhisper(recordScoreAndWhisper('eye-flap', scoreAcc))
    setBest(bestLocalScore('eye-flap'))
  }

  startFn.current = (d: Difficulty) => {
    const scale = difficultyScale(d)
    gapBase = 118 / scale
    speedBase = 140 * scale
    spawnEvery = 1.35 / scale
    setMenu(false)
    playing = true
    resetBoard()
  }

  restartFn.current = () => {
    setMenu(true)
    setOver(false)
    playing = false
    setBest(bestLocalScore('eye-flap'))
  }

  const flap = () => {
    if (!playing || gameOver) return
    started = true
    bird.vy = -320
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'ArrowUp') {
      e.preventDefault()
      flap()
    }
  }
  canvas.addEventListener('pointerdown', flap)
  window.addEventListener('keydown', onKey)

  const loop = (now: number) => {
    if (!alive) return
    const dt = Math.min(0.033, (now - last) / 1000)
    last = now
    const { w, h } = size()
    const th = themeRef.current

    if (playing && !gameOver && started) {
      bird.vy += 980 * dt
      bird.y += bird.vy * dt
      bird.ang = Math.max(-0.5, Math.min(0.7, bird.vy / 520))

      spawnAcc += dt
      if (spawnAcc > spawnEvery) {
        spawnAcc = 0
        const gap = gapBase - Math.min(28, scoreAcc * 2)
        pipes.push({
          x: w + 40,
          gapY: 90 + Math.random() * (h - 180),
          gap: Math.max(72, gap),
          scored: false
        })
      }
      for (const p of pipes) p.x -= (speedBase + scoreAcc * 4) * dt
      while (pipes.length && pipes[0]!.x < -40) pipes.shift()

      for (const p of pipes) {
        if (!p.scored && p.x + 18 < bird.x) {
          p.scored = true
          scoreAcc += 1
          setScore(scoreAcc)
        }
        const halfW = 14
        const topH = p.gapY - p.gap / 2
        const botY = p.gapY + p.gap / 2
        const inX = Math.abs(bird.x - p.x) < halfW + bird.size * 0.35
        if (inX && (bird.y < topH + bird.size * 0.3 || bird.y > botY - bird.size * 0.3)) {
          endRun()
        }
      }
      if (bird.y > h - 20 || bird.y < 16) endRun()
    }

    ctx.fillStyle = th.paper
    ctx.fillRect(0, 0, w, h)
    for (const p of pipes) {
      const topH = p.gapY - p.gap / 2
      const botY = p.gapY + p.gap / 2
      for (let y = 20; y < topH - 10; y += 28) {
        drawEyeCapsule(ctx, p.x, y, 16, 12, 0, th.body)
      }
      drawEyeCapsule(ctx, p.x, topH - 8, 18, 14, 0, th.mark)
      for (let y = botY + 10; y < h - 20; y += 28) {
        drawEyeCapsule(ctx, p.x, y, 16, 12, 0, th.body)
      }
      drawEyeCapsule(ctx, p.x, botY + 8, 18, 14, 0, th.mark)
    }
    if (playing) drawChevronShip(ctx, bird.x, bird.y, bird.size, bird.ang + Math.PI / 2, th.body)

    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)
  cleanup = () => {
    alive = false
    cancelAnimationFrame(raf)
    dispose()
    canvas.removeEventListener('pointerdown', flap)
    window.removeEventListener('keydown', onKey)
  }

    return () => {
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GameShell
      theme={theme}
      title="Flap"
      score={score}
      best={best}
      menu={menu}
      over={over}
      whisper={whisper}
      hint="Click / Space / ↑ to flap through the gaps."
      onStart={(d) => startFn.current?.(d)}
      onRestart={() => restartFn.current?.()}
    >
      <canvas ref={canvasRef} className="block touch-none" />
    </GameShell>
  )
}
